import React, { useState, useEffect } from "react";
import { supabase } from "../../utils/supabaseClient";
import { truncateWHT } from "../../utils/formatters";
import { withTimeout } from "../../utils/withTimeout";

const getInputCls = (isDarkMode, field, errors) =>
  `w-full p-3 h-12 rounded-xl border font-bold outline-none transition-colors ${
    errors[field] ? "border-rose-400" : ""
  } ${
    isDarkMode
      ? "bg-slate-900 border-slate-700 focus:border-sky-500"
      : "bg-slate-50 border-slate-200 focus:border-[#132B43]"
  }`;

export default function DividendForm({
  onClose,
  userId,
  onSaveSuccess,
  isDarkMode,
  editingItem,
  setNotification,
  themeConfig,
}) {
  const [formData, setFormData] = useState({
    id_stock: "",
    dividend_amount: "",
    date_dividend: new Date().toISOString().split("T")[0],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!editingItem) return;
    setFormData({
      id_stock:
        editingItem.id_stock ||
        editingItem.ID_Stock ||
        editingItem.symbol ||
        editingItem.stock ||
        "",
      dividend_amount:
        editingItem.dividend_amount ||
        editingItem.Dividend_Amount ||
        editingItem.gross ||
        "",
      date_dividend: editingItem.date_dividend
        ? editingItem.date_dividend.split("T")[0]
        : editingItem.Date_Dividend
        ? editingItem.Date_Dividend.split("T")[0]
        : new Date().toISOString().split("T")[0],
    });
  }, [editingItem]);

  const validate = () => {
    const e = {};
    if (!formData.id_stock.trim()) e.id_stock = "กรุณากรอกชื่อย่อหุ้น (Symbol)";
    if (!formData.dividend_amount || Number(formData.dividend_amount) <= 0)
      e.dividend_amount = "Enter a dividend amount greater than zero.";
    if (!formData.date_dividend) e.date_dividend = "กรุณาเลือกวันที่รับเงินปันผล";
    return e;
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSaving(true);

    try {
      const gross = Number(formData.dividend_amount);
      const tax   = gross < 0.08 ? 0 : truncateWHT(gross);
      const net   = Number((gross - tax).toFixed(2));

      const payload = {
        user_id:         userId,
        id_stock:        formData.id_stock.toUpperCase().trim(),
        dividend_amount: gross,
        wht_15:          tax,
        total_amount:    net,
        date_dividend:   formData.date_dividend,
        last_updated:    new Date().toISOString(),
      };
      if (editingItem?.id) payload.id = editingItem.id;

      const { data, error } = await withTimeout(
        supabase.from("dividends").upsert(payload).select().single()
      );

      if (error) throw error;

      if (setNotification)
        setNotification(editingItem?.id ? "Dividend updated." : "Dividend saved.");

      onClose();
      if (onSaveSuccess && data) onSaveSuccess(data);
    } catch (e) {
      console.error("Save Dividend Error:", e);
      if (setNotification) {
        if (e?.message === "TIMEOUT") {
          setNotification("ใช้เวลานานเกินกำหนด กรุณาตรวจการเชื่อมต่อแล้วลองอีกครั้ง");
        } else {
          setNotification("บันทึกเงินปันผลไม่สำเร็จ ข้อมูลที่กรอกไว้ยังอยู่ครบ");
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const inputCls = (field) => getInputCls(isDarkMode, field, errors);

  return (
    <div
      className={`p-6 md:p-8 rounded-xl border max-w-md w-full shadow-sm ${
        isDarkMode
          ? "bg-[#1E1F20] border-slate-700 text-white"
          : "bg-white border-slate-200 text-slate-900"
      }`}
    >
      <h3 className="text-2xl font-black mb-1">บันทึกเงินปันผล</h3>
      <p className="text-[13px] font-bold opacity-70 uppercase tracking-widest mb-6">
        บันทึกยอดปันผลก่อนหักภาษี
      </p>

      <div className="space-y-4">

        {/* Symbol */}
        <div>
          <label className="text-[11px] font-bold uppercase ml-1 opacity-50">ชื่อย่อหุ้น (Symbol)</label>
          <input
            placeholder="Ex. AAPL"
            className={inputCls("id_stock")}
            value={formData.id_stock}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, id_stock: e.target.value.toUpperCase() }));
              if (errors.id_stock) setErrors((prev) => ({ ...prev, id_stock: null }));
            }}
          />
          {errors.id_stock && (
            <p className="text-rose-400 text-[11px] font-bold mt-1 ml-1">{errors.id_stock}</p>
          )}
        </div>

        {/* Gross Dividend */}
        <div>
          <label className="text-[11px] font-bold uppercase ml-1 opacity-50">เงินปันผลก่อนหักภาษี (USD)</label>
          <input
            type="number"
            step="any"
            placeholder="0.00"
            className={inputCls("dividend_amount")}
            value={formData.dividend_amount}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, dividend_amount: e.target.value }));
              if (errors.dividend_amount) setErrors((prev) => ({ ...prev, dividend_amount: null }));
            }}
          />
          {errors.dividend_amount && (
            <p className="text-rose-400 text-[11px] font-bold mt-1 ml-1">{errors.dividend_amount}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label className="text-[11px] font-bold uppercase ml-1 opacity-50">วันที่รับเงิน</label>
          <input
            type="date"
            className={inputCls("date_dividend")}
            value={formData.date_dividend}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, date_dividend: e.target.value }));
              if (errors.date_dividend) setErrors((prev) => ({ ...prev, date_dividend: null }));
            }}
          />
          {errors.date_dividend && (
            <p className="text-rose-400 text-[11px] font-bold mt-1 ml-1">{errors.date_dividend}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className={`flex-1 p-3.5 h-12 rounded-xl font-bold uppercase text-xs transition-colors ${
              isDarkMode
                ? "bg-slate-800 text-slate-400 hover:bg-slate-700"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className={`flex-1 p-3.5 h-12 rounded-xl text-white font-bold uppercase text-xs disabled:opacity-50 transition-colors ${
              isDarkMode
                ? "bg-sky-500 hover:bg-sky-600"
                : "bg-[#132B43] hover:bg-[#132B43]/90"
            }`}
          >
            {isSaving ? "กำลังบันทึก..." : "บันทึก"}
          </button>
        </div>

      </div>
    </div>
  );
}
